import { useMemo } from "react";
import { useReviewStore, getReviewsForDevice, getAllReviews, type DeviceReview } from "@/stores/reviewStore";
import { Star, Play } from "lucide-react";
import { transformSupabaseImage } from "@/lib/imageTransform";

interface CustomerReviewsProps {
  deviceName?: string;
  /** Show all reviews (storefront section) vs device-specific */
  showAll?: boolean;
  maxItems?: number;
}

const ReviewMediaItem = ({ url, type }: { url: string; type: "image" | "video" }) => {
  if (type === "video") {
    return (
      <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0 cursor-pointer"
        onClick={() => {
          const w = window.open("", "_blank");
          if (w) {
            w.document.write(`<html><body style="margin:0;display:flex;align-items:center;justify-content:center;background:#000;height:100vh"><video src="${url}" controls autoplay style="max-width:100%;max-height:100%"></video></body></html>`);
          }
        }}>
        <video src={url} className="w-full h-full object-contain bg-muted" muted preload="metadata" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <Play className="w-4 h-4 text-white fill-white" />
        </div>
      </div>
    );
  }
  return (
    <img src={transformSupabaseImage(url, { width: 128, quality: 70 })} alt="Customer review" className="w-16 h-16 rounded-lg object-contain bg-muted flex-shrink-0" loading="lazy" decoding="async" />
  );
};

const ReviewCard = ({ review }: { review: DeviceReview }) => {
  const stars = (review as any).stars ?? 5;
  const verified = (review as any).verifiedBuyer === true;
  return (
    <div className="bg-background rounded-xl p-3 space-y-2 border border-border/30">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map(i => (
            <Star key={i} className={`w-3 h-3 ${i <= stars ? "text-amber-400 fill-amber-400" : "text-muted-foreground"}`} />
          ))}
        </div>
        <span className="text-xs font-semibold text-foreground">{review.customerName}</span>
        {verified && (
          <span
            title="Verified buyer — confirmed completed order for this device"
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[9px] font-bold uppercase tracking-wider"
          >
            ✓ Verified buyer
          </span>
        )}
      </div>
      {review.comment && (
        <p className="text-xs text-muted-foreground leading-relaxed">{review.comment}</p>
      )}
      {review.media.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {review.media.map(m => (
            <ReviewMediaItem key={m.id} url={m.url} type={m.type} />
          ))}
        </div>
      )}
      <p className="text-[10px] text-foreground/70">{review.deviceName}</p>
    </div>
  );
};

const CustomerReviews = ({ deviceName, showAll = false, maxItems = 6 }: CustomerReviewsProps) => {
  const reviewRev = useReviewStore(s => s._rev);

  const reviews = useMemo(() => {
    void reviewRev;
    if (showAll) {
      return getAllReviews().filter(r => r.enabled).slice(0, maxItems);
    }
    if (!deviceName) return [];
    return getReviewsForDevice(deviceName).slice(0, maxItems);
  }, [deviceName, showAll, maxItems, reviewRev]);

  if (reviews.length === 0) return null;

  return (
    <section className="bg-card rounded-2xl shadow-card p-4 sm:p-5 border border-border/50">
      <div className="flex items-center gap-2 mb-3">
        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
        <h3 className="font-display text-sm font-bold text-card-foreground">
          {showAll ? "Happy Customers" : "Customer Reviews"}
        </h3>
        <span className="text-[10px] text-muted-foreground ml-auto">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="space-y-2">
        {reviews.map(r => (
          <ReviewCard key={r.id} review={r} />
        ))}
      </div>
    </section>
  );
};

export default CustomerReviews;
